import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { isLoggedIn } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!isLoggedIn) {
      const local = JSON.parse(localStorage.getItem('amv_cart') || '[]');
      setItems(local); return;
    }
    try { const d = await api.get('/cart'); setItems(d.items || []); } catch {}
  }, [isLoggedIn]);

  useEffect(() => { load(); }, [load]);

  const add = async (product_id, variant_id = null, quantity = 1, meta = {}) => {
    if (!isLoggedIn) {
      setItems(prev => {
        const idx = prev.findIndex(i => i.product_id === product_id && i.variant_id === variant_id);
        const next = [...prev];
        if (idx > -1) next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        else next.push({ id: Date.now(), product_id, variant_id, quantity, ...meta });
        localStorage.setItem('amv_cart', JSON.stringify(next));
        return next;
      });
      toast.success('Added to cart');
      setOpen(true);
      return;
    }
    await api.post('/cart', { product_id, variant_id, quantity });
    await load();
    toast.success('Added to cart');
    setOpen(true);
  };

  const update = async (id, quantity) => {
    if (quantity < 1) return remove(id);
    if (!isLoggedIn) {
      setItems(prev => { const n = prev.map(i => i.id === id ? {...i, quantity} : i); localStorage.setItem('amv_cart', JSON.stringify(n)); return n; });
      return;
    }
    await api.put(`/cart/${id}`, { quantity });
    await load();
  };

  const remove = async (id) => {
    if (!isLoggedIn) {
      setItems(prev => { const n = prev.filter(i => i.id !== id); localStorage.setItem('amv_cart', JSON.stringify(n)); return n; });
      return;
    }
    await api.delete(`/cart/${id}`);
    await load();
  };

  const clear = async () => {
    if (!isLoggedIn) { setItems([]); localStorage.removeItem('amv_cart'); return; }
    await api.delete('/cart');
    setItems([]);
  };

  const total = items.reduce((s, i) => s + ((i.sale_price || i.price || 0) * i.quantity), 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, update, remove, clear, load, total, count, open, setOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
